import { useId, useMemo } from "react";
import type { ArtView } from "../types";

const POEM_LINES = ["不谢东君意", "丹青独立名", "莫嫌孤叶淡", "终久不凋零"] as const;
const COLUMN_X = [286, 214, 142, 70] as const;
const ROW_Y = [84, 169, 254, 339, 424] as const;
const MINI_LEAF_PATH = "M -8 0 C -3 -3.2, 4 -2.8, 9 0 C 4 2.7, -4 3, -8 0 Z";

interface PoemLeaf {
  x: number;
  y: number;
  rotation: number;
  opacity: number;
}

interface BranchSpec {
  start: readonly [number, number];
  end: readonly [number, number];
}

const DECOY_BRANCHES: BranchSpec[] = [
  { start: [30, 486], end: [139, 362] },
  { start: [38, 374], end: [126, 270] },
  { start: [43, 255], end: [132, 139] },
  { start: [331, 480], end: [229, 365] },
  { start: [325, 350], end: [234, 244] },
  { start: [319, 225], end: [232, 105] },
];

function branchLeaves(): PoemLeaf[] {
  return DECOY_BRANCHES.flatMap((branch, branchIndex) =>
    [0.28, 0.5, 0.7, 0.88].flatMap((t, sampleIndex) => {
      const dx = branch.end[0] - branch.start[0];
      const dy = branch.end[1] - branch.start[1];
      const x = branch.start[0] + dx * t;
      const y = branch.start[1] + dy * t;
      const tangent = (Math.atan2(dy, dx) * 180) / Math.PI;
      return [-1, 1].map((side) => ({
        x: x + side * 2,
        y: y - side * 2,
        rotation: tangent + side * (28 + (sampleIndex % 2) * 7),
        opacity: 0.6 + ((branchIndex + sampleIndex) % 3) * 0.09,
      }));
    }),
  );
}

function coreBranchLeaves(): PoemLeaf[] {
  return COLUMN_X.flatMap((centerX, columnIndex) =>
    [0, 1, 2, 3, 4, 5].flatMap((rowIndex) => {
      const y = 55 + rowIndex * 76;
      const x = centerX + Math.sin((rowIndex + columnIndex) * 1.7) * 8;
      const flow = columnIndex % 2 === 0 ? -18 : 58;
      return [-1, 1].map((side) => ({
        x: x + side * 3,
        y: y + side * 2,
        rotation: flow + side * (31 + (rowIndex % 2) * 6),
        opacity: 0.64 + ((rowIndex + columnIndex) % 3) * 0.08,
      }));
    }),
  );
}

function glyphLeaves(centerX: number, centerY: number, lineIndex: number): PoemLeaf[] {
  return Array.from({ length: 42 }, (_, index) => {
    const column = index % 7;
    const row = Math.floor(index / 7);
    return {
      x: centerX - 30 + column * 10,
      y: centerY - 27 + row * 10.5,
      rotation: (lineIndex % 2 === 0 ? -24 : 58) + ((index + lineIndex) % 3 - 1) * 11,
      opacity: 0.74 + ((index + lineIndex) % 4) * 0.06,
    };
  });
}

export function PoemBambooArt({ view }: { view: ArtView }) {
  const rawId = useId().replace(/:/g, "");
  const decoys = useMemo(() => [...branchLeaves(), ...coreBranchLeaves()], []);
  const glyphs = POEM_LINES.flatMap((line, lineIndex) =>
    Array.from(line).map((character, rowIndex) => ({
      character,
      lineIndex,
      rowIndex,
      x: COLUMN_X[lineIndex],
      y: ROW_Y[rowIndex],
      clipId: `poem-${rawId}-${lineIndex}-${rowIndex}`,
    })),
  );

  return (
    <svg
      className={`poem-bamboo-art poem-bamboo-art--${view}`}
      viewBox="0 0 360 520"
      role="img"
      aria-label={view === "painting" ? "双竹叶中藏着二十字诗" : `竹叶组成藏诗：${POEM_LINES.join("，")}`}
    >
      <defs aria-hidden="true">
        {glyphs.map((glyph) => (
          <clipPath id={glyph.clipId} key={glyph.clipId} clipPathUnits="userSpaceOnUse">
            <text x={glyph.x} y={glyph.y + 22} textAnchor="middle" className="poem-char-clip" aria-hidden="true">
              {glyph.character}
            </text>
          </clipPath>
        ))}
      </defs>

      <g className="poem-bamboo-stems" aria-hidden="true">
        <path d="M 28 512 C 42 392, 39 245, 53 24" />
        <path d="M 333 512 C 316 383, 327 224, 306 16" />
        {COLUMN_X.map((x, index) => (
          <path key={`core-${x}`} className="poem-core-branch" d={`M ${x - 5} 493 C ${x + (index % 2 === 0 ? 14 : -14)} 352, ${x - (index % 2 === 0 ? 12 : -12)} 190, ${x + 5} 29`} />
        ))}
        {DECOY_BRANCHES.map((branch, index) => (
          <path key={index} d={`M ${branch.start[0]} ${branch.start[1]} Q ${(branch.start[0] + branch.end[0]) / 2} ${(branch.start[1] + branch.end[1]) / 2 - 12} ${branch.end[0]} ${branch.end[1]}`} />
        ))}
      </g>

      <g className="poem-decoy-leaves" aria-hidden="true">
        {decoys.map((leaf, index) => (
          <path key={index} className="ink-leaf" d={MINI_LEAF_PATH} opacity={leaf.opacity} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotation}) scale(1.18)`} />
        ))}
      </g>

      {glyphs.map((glyph) => (
        <g className="poem-glyph" data-character={glyph.character} data-line={glyph.lineIndex} key={glyph.clipId} aria-hidden="true">
          <text x={glyph.x} y={glyph.y + 22} textAnchor="middle" className="poem-glyph-ghost" aria-hidden="true">
            {glyph.character}
          </text>
          <g className="poem-glyph-leaves" clipPath={`url(#${glyph.clipId})`}>
            {glyphLeaves(glyph.x, glyph.y, glyph.lineIndex).map((leaf, index) => (
              <path key={index} className="ink-leaf" d={MINI_LEAF_PATH} opacity={leaf.opacity} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotation})`} />
            ))}
          </g>
        </g>
      ))}

      {view === "meaning" && (
        <g className="poem-meaning-seal" aria-hidden="true">
          <circle cx="180" cy="480" r="18" />
          <text x="180" y="486" textAnchor="middle">志</text>
        </g>
      )}
    </svg>
  );
}
